const graphql = require('./graphql');
const { forOwn } = require('lodash');
const dataCache = require('./dataCache');
const { CacheType } = dataCache;
const queryHelper = require('./queryHelper');
const AsyncLock = require('async-lock');
const { pascalCase } = require('change-case');
const VersionMap = require('./VersionMap');

const TypeRefFragment = `
	fragment TypeRef on __Type {
		kind
		name
		ofType {
			kind
			name
			ofType {
				kind
				name
				ofType {
					kind
					name
					ofType {
						kind
						name
							ofType {
							kind
							name
							ofType {
								kind
								name
								ofType {
									kind
									name
								}
							}
						}
					}
				}
			}
		}
	}`;

const GraphQLTypeFragment = `
	fragment GraphQLTypeFragment on __Type {
		kind
		name
		fields(includeDeprecated: true) {
			name
			type {
				...TypeRef
			}
		}
		inputFields {
			name
			type {
				...TypeRef
			}
		}
		enumValues(includeDeprecated: true) {
			name
		}
	} ${TypeRefFragment}`;

const TYPE_PLACEHOLDER = '$type';
const FETCH_TYPE_PLACEHOLDER = '$fetchType';

const FETCH_TYPE = `
		${TYPE_PLACEHOLDER}:__type(name: "${TYPE_PLACEHOLDER}") {
			...GraphQLTypeFragment
		}
`;

const FETCH_TYPES = `
	query {
		${FETCH_TYPE_PLACEHOLDER}
	} ${GraphQLTypeFragment}
`;

const GraphQLFieldKinds = {
	Enum: 'ENUM',
	InputObject: 'INPUT_OBJECT',
	List: 'LIST',
	NonNull: 'NON_NULL',
	Object: 'OBJECT',
	Scalar: 'SCALAR',
};

const GraphQLScalarTypes = {
	DateFilter: 'DateFilter',
	IDFilter: 'IDFilter',
	IntFilter: 'IntFilter',
	StringFilter: 'StringFilter',
	Date: 'Date',
	ID: 'ID',
	Int: 'Int',
	String: 'String',
};

const SchemaTypes = {
	Filter: 'Filter',
	Select: 'Select',
};

const attributePathsToIgnore = [
	'metadata',
	'AND',
	'OR',
	'ALL',
	'NIN',
	'IN',
	'NULL',
];

/**
 * Create a cache key using the graphQL url (which includes version) as well as the schema type.
 */
const getCacheKey = (api, graphType) => {
	if (!api || !api.cacheKey) { return ''; }
	graphType = graphType || '';
	return `${api.cacheKey}_${graphType}`;
};

/**
 * Fetch ethos metadata types from data access GraphQL api introspection by base name.
 */
const fetchTypesByName = async (api, typeNames, ignoreMetaAttributes, schemaType = SchemaTypes.Filter) => {
	if (!typeNames || !typeNames.length) { return null; }
	let results = [];
	for (const type of typeNames) {
		try {
			const graphType = schemaType === SchemaTypes.Filter ? getFetchTypeName(type) : type;
			const graphTypemaxVersion = await getMaxVersionNameForBaseName(api, graphType, schemaType);
			if (graphTypemaxVersion) {
				let result = await fetchTypes(api, [graphTypemaxVersion]);
				if (result && result.length) {
					const fieldProp = schemaType === SchemaTypes.Filter ? 'inputFields' : 'fields';
					if (result[0][fieldProp] && ignoreMetaAttributes) {
						result[0][fieldProp] = result[0][fieldProp].filter(attr => !attributePathsToIgnore.includes(attr.name));
					}
				}
				results.push(result[0]);
			}
		} catch (error) {
			throw error;
		}
	}
	return results;
};

/**
 * Fetch ethos metadata types from data access GraphQL api introspection.
 */
const fetchTypes = async (api, graphTypes) => {
	if (!api || (!api.webFetch && !api.apiFetch) || !graphTypes || !graphTypes.length) {
		return [];
	}

	// distinct
	graphTypes = Array.from(new Set(graphTypes));

	const uncachedGraphTypes = [];
	const cachedTypes = [];

	// look in cache to see if we have any of the desired types already
	for (const graphType of graphTypes) {
		const cachedType = await dataCache.getItem(CacheType.Memory, getCacheKey(api, graphType));
		if (cachedType) {
			cachedTypes.push(cachedType);
		} else {
			uncachedGraphTypes.push(graphType);
		}
	}

	graphTypes = uncachedGraphTypes;

	if (graphTypes.length === 0) { // everything already cached
		return cachedTypes;
	}

	let query = FETCH_TYPES;

	// build one query for each ethosType
	for (let i = 0; i < graphTypes.length; i++) {
		const ethosType = graphTypes[i];
		const fetch = FETCH_TYPE.split(TYPE_PLACEHOLDER).join(ethosType);
		query = query.replace(FETCH_TYPE_PLACEHOLDER, `${fetch}
			${FETCH_TYPE_PLACEHOLDER}`);
	}
	query = query.replace(FETCH_TYPE_PLACEHOLDER, '');

	try {
		const response = await graphql.query({
			query,
			api,
		});
		const { data, errors } = queryHelper.response(response);

		if (errors) {
			console.log('Introspection error for query ' + JSON.stringify(query) + ' ' + JSON.stringify(errors));
			throw errors;
		}

		const types = [...cachedTypes];

		forOwn(data, (type) => {
			if (type) {
				dataCache.setItem(CacheType.Memory, getCacheKey(api, type.name), type);
				types.push(type);
			}
		});

		return types;
	} catch (error) {
		throw error;
	}
};

const lock = new AsyncLock();
const rootTypeNamesMapCache = new Map();

/**
 * Fetch the root type names into a VersionMap via introspection. This is costly so the query
 * should only execute once per session.
 */
const fetchRootTypeNames = async (api) => {
	const cacheKey = getRootTypeNamesMapCacheKey(api);
	const result = await lock.acquire(cacheKey, async (done) => {
		try {
			let rootTypeNamesMap = rootTypeNamesMapCache.get(cacheKey);
			if (rootTypeNamesMap) {
				done(null, rootTypeNamesMap);
				return;
			}
			const rootTypes = await fetchTypes(api, ['Query']);
			const rootType = rootTypes[0];
			let rootTypesNames = [];
			if (rootType && rootType.fields) {
				rootTypesNames = rootType.fields
					.map((f) => f.type.name)
					.map((name) => `F_${pascalCase(name.substring(0, name.indexOf('Connection')))}`);
			}
			rootTypeNamesMap = new VersionMap(rootTypesNames);
			rootTypeNamesMapCache.set(cacheKey, rootTypeNamesMap);

			done(null, rootTypeNamesMap);
		} catch (error) {
			done(error, null);
		}
	});
	return result;
};

const getFetchTypeName = (graphType) => {
	return `F_${graphType}`;
};

const getSelectTypeName = (graphType) => {
	if (graphType.startsWith('F_')) {
		return graphType.substring(2);
	}
	return graphType;
};

const getRootTypeNamesMapCacheKey = (api) => {
	return `${api.cacheKey}_rootTypeNamesMap`;
};

/**
 * For the given unversioned model base name like `F_ModelName`, return the max version name supported such as
 * `F_ModelName12`.
 */
const getMaxVersionNameForBaseName = async (api, graphType, schemaType = SchemaTypes.Filter) => {
	const rootTypeNamesMap = await fetchRootTypeNames(api);
	if (schemaType === SchemaTypes.Filter) {
		return rootTypeNamesMap.getMaxVersionNameForBaseName(graphType);
	}
	// Add "F_" to get the name and version and then strip it off
	if (schemaType === SchemaTypes.Select) {
		const result = rootTypeNamesMap.getMaxVersionNameForBaseName(getFetchTypeName(graphType));
		return getSelectTypeName(result);
	}
};

/**
 * For the given unversioned model base name like `F_ModelName`, return the max version supported such as
 * 12.
 */
const getMaxVersionForBaseName = async (api, graphType, schemaType = SchemaTypes.Filter) => {
	const rootTypeNamesMap = await fetchRootTypeNames(api);
	const fetchType = schemaType === SchemaTypes.Select ? getFetchTypeName(graphType) : graphType;
	return rootTypeNamesMap.getMaxVersionForBaseName(fetchType);
};

const getMaxVersionForBaseNameInputField = async (api, graphTypeName, inputFieldsNames, schemaType = SchemaTypes.Filter) => {
	if (!api || !graphTypeName || !Array.isArray(inputFieldsNames) || !inputFieldsNames.length > 0) {
		return {};
	}
	const inputFieldsNamesMaxVersion = {}
	try {
		const graphType = schemaType === SchemaTypes.Filter ? getFetchTypeName(graphTypeName) : graphTypeName;
		const graphTypeMaxVersion = await getMaxVersionNameForBaseName(api, graphType, schemaType);
		if (graphTypeMaxVersion) {
			let results = await fetchTypes(api, [graphTypeMaxVersion]);
			if (results && results.length > 0) {
				const fields = results[0].inputFields
				for (const field of fields) {
					for (const fieldName of inputFieldsNames) {
						const matchInputFieldNameRegx = new RegExp(`^${fieldName}[0-9]*$`, 'g')
						if (matchInputFieldNameRegx.test(field.name)) {
							inputFieldsNamesMaxVersion[fieldName] = field.name
						}
					}
				}
			}
		}
		return inputFieldsNamesMaxVersion
	}catch (error){
		throw error;
	}
}

module.exports = {
	GraphQLFieldKinds,
	GraphQLScalarTypes,
	fetchRootTypeNames,
	fetchTypes,
	fetchTypesByName,
	getCacheKey,
	getFetchTypeName,
	getMaxVersionNameForBaseName,
	getMaxVersionForBaseName,
	getSelectTypeName,
	getMaxVersionForBaseNameInputField,
	SchemaTypes,
};